import { Box, Button, makeStyles, Modal, Theme, Typography, useMediaQuery } from "@material-ui/core";
import dayjs from "dayjs";
import localeData from "dayjs/plugin/localeData";
import {
  createContext,
  forwardRef,
  ForwardRefExoticComponent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker-cssmodules.css";
import "react-datepicker/dist/react-datepicker.css";

import Loader from "../../../../components/Loader";
import { truncateText } from "../../../../utils/generic";
import { courtDocRef } from "../../../../utils/referencies";
import { ICellContext, ICourt } from "../../types";
import { getCourt } from "../../utils/queries";
import CalendarGridCell from "./CalendarGridCell";
import CellManage from "./CellManage";
import { CalendarContext } from "./index";

dayjs.extend(localeData);

const useStyles = makeStyles((theme) => ({
  container: {
    display: "grid",
    marginTop: theme.spacing(2),
  },
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: theme.zIndex.drawer + 1,
  },
}));

const CalendarDatePickerButton: ForwardRefExoticComponent<any> = forwardRef<any, any>(({ value, onClick }, ref) => (
  <Button variant="contained" color="primary" onClick={onClick} ref={ref}>
    {value}
  </Button>
));

export const CellContext = createContext<ICellContext>({
  selectedCellDate: undefined,
  minSlot: undefined,
  closeModal() {},
});

const CalendarGrid = () => {
  const firstDayInCurrentMonth = useMemo(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1), []);
  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

  const classes = useStyles({});
  const [month, setMonth] = useState<number>(0);
  const [date, setDate] = useState(firstDayInCurrentMonth);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCellDate, setSelectedCellDate] = useState<Date | undefined>(undefined);
  const [court, setCourt] = useState<ICourt | null>(null);
  const [loading, setLoading] = useState(false);
  const { courtId } = useContext(CalendarContext);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedCellDate(undefined);
  }, []);

  const handleOpenModal = (selectedDay: Date) => {
    setIsModalOpen(!isModalOpen);
    setSelectedCellDate(selectedDay);
  };

  const handleDateChange = (date: Date) => {
    setDate(date);
  };

  useEffect(() => {
    setMonth(dayjs(date).month());
  }, [date]);

  useEffect(() => {
    setLoading(true);
    const getCourtSubscription = getCourt(courtDocRef(courtId)).subscribe({
      next(data) {
        setCourt(data);
        setLoading(false);
      },
    });

    return () => {
      getCourtSubscription.unsubscribe();
    };
  }, []);

  return (
    <Loader loading={loading}>
      {court && (
        <>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h5" color="textSecondary" style={{ width: "auto" }}>
              {truncateText(court.name.en)}
            </Typography>

            <Box>
              <DatePicker
                showMonthYearPicker
                minDate={firstDayInCurrentMonth}
                dateFormat="MMMM, yyyy"
                selected={date}
                onChange={handleDateChange}
                customInput={<CalendarDatePickerButton />}
                showPopperArrow={false}
              />
            </Box>
          </Box>
          <Box
            className={classes.container}
            gridTemplateColumns={isSm ? "repeat(2, 1fr)" : "repeat(7, 1fr)"}
            gridTemplateRows={isSm ? "repeat(5, 100px)" : "repeat(5, 150px)"}
          >
            {Array(dayjs().month(month).daysInMonth())
              .fill(0)
              .map((_, i) => (
                <CalendarGridCell key={`${i}`} date={date} index={i} onOpenModal={handleOpenModal} />
              ))}
          </Box>
          <Modal
            className={classes.modal}
            open={isModalOpen}
            onClose={handleCloseModal}
            children={
              <CellContext.Provider
                value={{
                  selectedCellDate,
                  minSlot: court.defaultSlotTime,
                  closeModal: handleCloseModal,
                }}
              >
                <CellManage />
              </CellContext.Provider>
            }
            disableBackdropClick={true}
          />
        </>
      )}
    </Loader>
  );
};

export default CalendarGrid;
